"use strict";
'use client';

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PopoverPopup = void 0;
var React = _interopRequireWildcard(require("react"));
var _floatingUiReact = require("../../floating-ui-react");
var _PopoverRootContext = require("../root/PopoverRootContext");
var _PopoverPositionerContext = require("../positioner/PopoverPositionerContext");
var _popupStateMapping = require("../../utils/popupStateMapping");
var _stateAttributesMapping = require("../../utils/stateAttributesMapping");
var _useOpenChangeComplete = require("../../utils/useOpenChangeComplete");
var _useRenderElement = require("../../utils/useRenderElement");
var _reasons = require("../../utils/reasons");
var _usePopupAutoResize = require("../../utils/usePopupAutoResize");
var _constants = require("../../utils/constants");
var _DirectionContext = require("../../direction-provider/DirectionContext");
var _composite = require("../../composite/composite");
var _ToolbarRootContext = require("../../toolbar/root/ToolbarRootContext");
var _jsxRuntime = require("react/jsx-runtime");
const stateAttributesMapping = {
  ..._popupStateMapping.popupStateMapping,
  ..._stateAttributesMapping.transitionStatusMapping
};

/**
 * A container for the popover contents.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Popover](https://base-ui.com/react/components/popover)
 */
const PopoverPopup = exports.PopoverPopup = /*#__PURE__*/React.forwardRef(function PopoverPopup(componentProps, forwardedRef) {
  const {
    className,
    render,
    initialFocus,
    finalFocus,
    ...elementProps
  } = componentProps;
  const {
    store
  } = (0, _PopoverRootContext.usePopoverRootContext)();
  const positioner = (0, _PopoverPositionerContext.usePopoverPositionerContext)();
  const insideToolbar = (0, _ToolbarRootContext.useToolbarRootContext)(true) != null;
  const direction = (0, _DirectionContext.useDirection)();
  const open = store.useState('open');
  const openMethod = store.useState('openMethod');
  const instantType = store.useState('instantType');
  const transitionStatus = store.useState('transitionStatus');
  const popupProps = store.useState('popupProps');
  const titleId = store.useState('titleElementId');
  const descriptionId = store.useState('descriptionElementId');
  const modal = store.useState('modal');
  const mounted = store.useState('mounted');
  const openReason = store.useState('openReason');
  const popupElement = store.useState('popupElement');
  const triggers = store.useState('triggers');
  const payload = store.useState('payload');
  const positionerElement = store.useState('positionerElement');
  const activeTriggerElement = store.useState('activeTriggerElement');
  const floatingContext = store.useState('floatingRootContext');
  (0, _useOpenChangeComplete.useOpenChangeComplete)({
    open,
    ref: store.context.popupRef,
    onComplete() {
      if (open) {
        store.context.onOpenChangeComplete?.(true);
      }
    }
  });

  // Default initial focus logic:
  // If opened by touch, focus the popup element to prevent the virtual keyboard from opening
  // (this is required for Android specifically as iOS handles this automatically).
  function defaultInitialFocus(interactionType) {
    if (interactionType === 'touch') {
      return store.context.popupRef.current;
    }
    return true;
  }
  const resolvedInitialFocus = initialFocus === undefined ? defaultInitialFocus : initialFocus;
  const state = React.useMemo(() => ({
    open,
    side: positioner.side,
    align: positioner.align,
    instant: instantType,
    transitionStatus
  }), [open, positioner.side, positioner.align, instantType, transitionStatus]);
  const setPopupElement = React.useCallback(element => {
    store.set('popupElement', element);
  }, [store]);
  function handleMeasureLayout() {
    floatingContext.events.emit('measure-layout');
  }
  function handleMeasureLayoutComplete(previousDimensions, nextDimensions) {
    floatingContext.events.emit('measure-layout-complete', {
      previousDimensions,
      nextDimensions
    });
  }

  // If there's just one trigger, we can skip the auto-resize logic as
  // the popover will always be anchored to the same position.
  const autoresizeEnabled = triggers.size > 1;
  (0, _usePopupAutoResize.usePopupAutoResize)({
    popupElement,
    positionerElement,
    mounted,
    content: payload,
    enabled: autoresizeEnabled,
    onMeasureLayout: handleMeasureLayout,
    onMeasureLayoutComplete: handleMeasureLayoutComplete
  });
  const anchoringStyles = React.useMemo(() => {
    if (!autoresizeEnabled) {
      return _constants.EMPTY_OBJECT;
    }

    // Ensure popup size transitions correctly when anchored to `bottom` (side=top) or `right` (side=left).
    let isOriginSide = positioner.side === 'top';
    let isPhysicalLeft = positioner.side === 'left';
    if (direction === 'rtl') {
      isOriginSide = isOriginSide || positioner.side === 'inline-end';
      isPhysicalLeft = isPhysicalLeft || positioner.side === 'inline-end';
    } else {
      isOriginSide = isOriginSide || positioner.side === 'inline-start';
      isPhysicalLeft = isPhysicalLeft || positioner.side === 'inline-start';
    }
    return isOriginSide ? {
      position: 'absolute',
      [positioner.side === 'top' ? 'bottom' : 'top']: '0',
      [isPhysicalLeft ? 'right' : 'left']: '0'
    } : _constants.EMPTY_OBJECT;
  }, [positioner.side, direction, autoresizeEnabled]);
  const element = (0, _useRenderElement.useRenderElement)('div', componentProps, {
    state,
    ref: [forwardedRef, store.context.popupRef, setPopupElement],
    props: [popupProps, {
      'aria-labelledby': titleId,
      'aria-describedby': descriptionId,
      style: anchoringStyles,
      onKeyDown(event) {
        if (insideToolbar && _composite.COMPOSITE_KEYS.has(event.key)) {
          event.stopPropagation();
        }
      }
    }, transitionStatus === 'starting' ? _constants.DISABLED_TRANSITIONS_STYLE : _constants.EMPTY_OBJECT, elementProps],
    stateAttributesMapping
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_floatingUiReact.FloatingFocusManager, {
    context: positioner.context,
    openInteractionType: openMethod,
    modal: modal === 'trap-focus',
    disabled: !mounted || openReason === _reasons.REASONS.triggerHover,
    initialFocus: resolvedInitialFocus,
    returnFocus: finalFocus,
    restoreFocus: "popup",
    previousFocusableElement: activeTriggerElement,
    nextFocusableElement: store.context.triggerFocusTargetRef,
    beforeContentFocusGuardRef: store.context.beforeContentFocusGuardRef,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") PopoverPopup.displayName = "PopoverPopup";